import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";

const NAV_ITEMS = [
  {
    key: "dashboard",
    path: "/app",
    icon: "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6",
  },
  {
    key: "clients",
    path: "/app/clients",
    icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z",
  },
  {
    key: "schedule",
    path: "/app/schedule",
    icon: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z",
  },
  {
    key: "haccp",
    path: "/app/haccp",
    icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
  },
];

const OWNER_NAV_ITEMS = [
  {
    key: "team",
    path: "/app/settings/team",
    icon: "M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z",
  },
  {
    key: "billing",
    path: "/app/settings/billing",
    icon: "M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z",
  },
  {
    key: "integrations",
    path: "/app/settings/integrations",
    icon: "M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m9.86-2.813a4.5 4.5 0 00-1.242-7.244l-4.5-4.5a4.5 4.5 0 00-6.364 6.364L4.25 8.188",
  },
];

export function AppShell({
  children,
  userName,
  userRole = "owner",
  onLogout,
}: {
  children: React.ReactNode;
  userName?: string;
  userRole?: "owner" | "technician";
  onLogout: () => void;
}) {
  const { t } = useTranslation();
  const location = useLocation();

  return (
    <div className="min-h-screen flex bg-navy-50">
      {/* ── Sidebar ── */}
      <aside className="w-60 bg-navy-900 flex flex-col shrink-0">
        {/* Logo */}
        <div className="px-5 h-16 flex items-center gap-2.5 border-b border-white/5">
          <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center">
            <span className="text-navy-950 font-bold text-sm" style={{ fontFamily: "var(--font-display)" }}>K</span>
          </div>
          <div>
            <span className="text-white font-bold text-base tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
              Kverna
            </span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 px-3 space-y-1">
          {NAV_ITEMS.map((item) => {
            const isActive =
              item.path === "/app"
                ? location.pathname === "/app"
                : location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.key}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-150 ${
                  isActive
                    ? "bg-white/10 text-white font-medium nav-active"
                    : "text-navy-400 hover:text-navy-200 hover:bg-white/5"
                }`}
              >
                <svg
                  className="w-[18px] h-[18px] shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d={item.icon} />
                </svg>
                {t(`nav.${item.key}`)}
              </Link>
            );
          })}

          {userRole === "owner" && (
            <>
              <div className="pt-4 pb-1 px-3">
                <p className="text-[10px] font-semibold text-navy-600 uppercase tracking-wider">
                  {t("nav.settings")}
                </p>
              </div>
              {OWNER_NAV_ITEMS.map((item) => {
                const isActive = location.pathname.startsWith(item.path);
                return (
                  <Link
                    key={item.key}
                    to={item.path}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-150 ${
                      isActive
                        ? "bg-white/10 text-white font-medium nav-active"
                        : "text-navy-400 hover:text-navy-200 hover:bg-white/5"
                    }`}
                  >
                    <svg
                      className="w-[18px] h-[18px] shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d={item.icon} />
                    </svg>
                    {t(`nav.${item.key}`)}
                  </Link>
                );
              })}
            </>
          )}
        </nav>

        {/* Language switcher */}
        <div className="px-3 py-2 border-t border-white/5">
          <div className="flex rounded-lg bg-white/5 p-0.5">
            {(["en", "pl"] as const).map((lng) => (
              <button
                key={lng}
                onClick={() => { i18n.changeLanguage(lng); localStorage.setItem("kverna-lng", lng); }}
                className={`flex-1 text-xs font-medium py-1.5 rounded-md transition-all ${
                  i18n.language === lng
                    ? "bg-white/10 text-white"
                    : "text-navy-500 hover:text-navy-300"
                }`}
              >
                {lng === "en" ? "EN" : "PL"}
              </button>
            ))}
          </div>
        </div>

        {/* User section */}
        <div className="px-3 py-4 border-t border-white/5 space-y-3">
          <div className="flex items-center gap-3 px-2">
            <div className="w-8 h-8 rounded-full bg-navy-700 flex items-center justify-center text-xs font-medium text-navy-300">
              {userName?.charAt(0).toUpperCase() ?? "?"}
            </div>
            <p className="text-sm text-navy-200 truncate flex-1 min-w-0">{userName}</p>
          </div>
          <button
            onClick={onLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-navy-400 hover:text-rose-400 hover:bg-white/5 transition-all duration-150"
          >
            <svg className="w-[18px] h-[18px] shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
            </svg>
            {t("nav.logout")}
          </button>
        </div>
      </aside>

      {/* ── Main content ── */}
      <main className="flex-1 overflow-auto">
        <div className="max-w-5xl mx-auto px-8 py-8">
          {children}
        </div>
      </main>
    </div>
  );
}
